import { ApiService } from '../../../services/api';
import { generateAsyncActionNames } from '../../../utils/helpers';
import { CONSTANTS } from '../../../utils/constants';
import { AppDispatch, RootState } from '../../type';
import { TableActions } from '../table/table.actions';
import { getErrorMessages } from '../../../utils/formatters';
import { IApiError } from '../../../models/api';
import { ISubCustomersRequest } from '../../../models/subCustomersRequest';
import { GetSubCustomersRequestsPayload } from './type';

const { get, put } = ApiService;

enum SubCustomersRequestsApiEndpoints {
  GET_SUB_CUSTOMERS_REQUESTS = `SubCustomersRequest/Get`,
  GET_SUB_CUSTOMERS_REQUEST_BY_ID = `SubCustomersRequest/GetById`,
  UPDATE_SUB_CUSTOMERS_REQUEST = `SubCustomersRequest/Update`,
}

enum SubCustomersRequestsActionsEnum {
  GET_SUB_CUSTOMERS_REQUESTS = 'GET_SUB_CUSTOMERS_REQUESTS',
  GET_SUB_CUSTOMERS_REQUEST_BY_ID = 'GET_SUB_CUSTOMERS_REQUEST_BY_ID',
  UPDATE_SUB_CUSTOMERS_REQUEST = 'UPDATE_SUB_CUSTOMERS_REQUEST',
}

export const SubCustomersRequestsActionsNames = {
  [SubCustomersRequestsActionsEnum.GET_SUB_CUSTOMERS_REQUESTS]: generateAsyncActionNames(
    SubCustomersRequestsActionsEnum.GET_SUB_CUSTOMERS_REQUESTS
  ),
  [SubCustomersRequestsActionsEnum.GET_SUB_CUSTOMERS_REQUEST_BY_ID]: generateAsyncActionNames(
    SubCustomersRequestsActionsEnum.GET_SUB_CUSTOMERS_REQUEST_BY_ID
  ),
  [SubCustomersRequestsActionsEnum.UPDATE_SUB_CUSTOMERS_REQUEST]: generateAsyncActionNames(
    SubCustomersRequestsActionsEnum.UPDATE_SUB_CUSTOMERS_REQUEST
  ),
};

const Actions = {
  [SubCustomersRequestsActionsEnum.GET_SUB_CUSTOMERS_REQUESTS]: {
    START: () => ({
      type: SubCustomersRequestsActionsNames.GET_SUB_CUSTOMERS_REQUESTS.START,
    }),
    FULFILLED: (data: GetSubCustomersRequestsPayload) => ({
      type: SubCustomersRequestsActionsNames.GET_SUB_CUSTOMERS_REQUESTS.FULFILLED,
      payload: data,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: SubCustomersRequestsActionsNames.GET_SUB_CUSTOMERS_REQUESTS.REJECTED,
      payload: error,
    }),
  },
  [SubCustomersRequestsActionsEnum.GET_SUB_CUSTOMERS_REQUEST_BY_ID]: {
    START: () => ({
      type: SubCustomersRequestsActionsNames.GET_SUB_CUSTOMERS_REQUEST_BY_ID.START,
    }),
    FULFILLED: (data: ISubCustomersRequest) => ({
      type: SubCustomersRequestsActionsNames.GET_SUB_CUSTOMERS_REQUEST_BY_ID.FULFILLED,
      payload: data,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: SubCustomersRequestsActionsNames.GET_SUB_CUSTOMERS_REQUEST_BY_ID.REJECTED,
      payload: error,
    }),
  },
  [SubCustomersRequestsActionsEnum.UPDATE_SUB_CUSTOMERS_REQUEST]: {
    START: () => ({
      type: SubCustomersRequestsActionsNames.UPDATE_SUB_CUSTOMERS_REQUEST.START,
    }),
    FULFILLED: () => ({
      type: SubCustomersRequestsActionsNames.UPDATE_SUB_CUSTOMERS_REQUEST.FULFILLED,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: SubCustomersRequestsActionsNames.UPDATE_SUB_CUSTOMERS_REQUEST.REJECTED,
      payload: error,
    }),
  },
};

const getSubCustomersRequestById =
  (id: number) =>
  async (dispatch: AppDispatch) => {
    dispatch(Actions[SubCustomersRequestsActionsEnum.GET_SUB_CUSTOMERS_REQUEST_BY_ID].START());
    try {
      const { data } = await get(
        `${SubCustomersRequestsApiEndpoints.GET_SUB_CUSTOMERS_REQUEST_BY_ID}/${id}`
      );
      const item: ISubCustomersRequest | null = data?.content?.subCustomersRequest ?? null;
      if (item) {
        dispatch(Actions[SubCustomersRequestsActionsEnum.GET_SUB_CUSTOMERS_REQUEST_BY_ID].FULFILLED(item));
      } else {
        dispatch(Actions[SubCustomersRequestsActionsEnum.GET_SUB_CUSTOMERS_REQUEST_BY_ID].REJECTED(null));
      }
      return item;
    } catch (error: unknown) {
      dispatch(Actions[SubCustomersRequestsActionsEnum.GET_SUB_CUSTOMERS_REQUEST_BY_ID].REJECTED(null));
      return null;
    }
  };

const getSubCustomersRequests =
  (
    params?: URLSearchParams | Record<string, unknown> | null,
    sort?: unknown[],
    updateTable = true,
    pageNumber: number = CONSTANTS.GRIDS.FIRST_PAGE_NUMBER,
    pageSize?: number | null
  ) =>
  async (dispatch: AppDispatch, getState?: () => RootState) => {
    dispatch(
      Actions[SubCustomersRequestsActionsEnum.GET_SUB_CUSTOMERS_REQUESTS].START()
    );

    const requestData: { params: URLSearchParams | Record<string, unknown> | null | undefined; headers: Record<string, string> } = {
      params,
      headers: {
        'X-Pagination': JSON.stringify({
          PageNumber: pageNumber,
          PageSize:
            pageSize ?? getState?.()?.table.pageSize ?? CONSTANTS.GRIDS.PAGE_SIZE,
        }),
      },
    };

    if (sort && sort.length) {
      requestData.headers['X-Sorting'] = JSON.stringify({
        Sortings: sort,
      });
    }

    try {
      const { data } = await get(
        SubCustomersRequestsApiEndpoints.GET_SUB_CUSTOMERS_REQUESTS,
        requestData
      );

      const subCustomersRequests = data?.content?.subCustomersRequests;

      dispatch(
        Actions[SubCustomersRequestsActionsEnum.GET_SUB_CUSTOMERS_REQUESTS].FULFILLED(
          subCustomersRequests
        )
      );

      if (updateTable) {
        dispatch(
          TableActions.setTableData({
            totalRows: data?.content?.totalCount,
            pageNumber: CONSTANTS.GRIDS.FIRST_PAGE_NUMBER,
          })
        );
      }
      return subCustomersRequests;
    } catch (error: unknown) {
      dispatch(
        Actions[SubCustomersRequestsActionsEnum.GET_SUB_CUSTOMERS_REQUESTS].REJECTED(
          getErrorMessages(error)
        )
      );
    }
  };

const updateSubCustomersRequest =
  (id: number, body: { requestStatus: string }) =>
  async (dispatch: AppDispatch) => {
    dispatch(Actions[SubCustomersRequestsActionsEnum.UPDATE_SUB_CUSTOMERS_REQUEST].START());
    try {
      const { data } = await put(
        `${SubCustomersRequestsApiEndpoints.UPDATE_SUB_CUSTOMERS_REQUEST}/${id}`,
        body
      );
      if (!data.isSucceeded) throw Error(JSON.stringify(data.errors));
      dispatch(Actions[SubCustomersRequestsActionsEnum.UPDATE_SUB_CUSTOMERS_REQUEST].FULFILLED());
      return data?.content;
    } catch (error: unknown) {
      dispatch(
        Actions[SubCustomersRequestsActionsEnum.UPDATE_SUB_CUSTOMERS_REQUEST].REJECTED(
          getErrorMessages(error)
        )
      );
    }
  };

export const SubCustomersRequestsActions = {
  getSubCustomersRequests,
  getSubCustomersRequestById,
  updateSubCustomersRequest,
};
